import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Grid,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ArrowBack,
  LocationOn,
  People,
  Euro,
  AccessTime,
  Person,
  Schedule,
  CheckCircle,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import axios from '../config/axios';
import dayjs from 'dayjs';
import ModalResult from '../components/ModalResult';
import ModalConfirm from '../components/ModalConfirm';

const ReservationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [reservable, setReservable] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalSeverity, setModalSeverity] = useState('info');
  const [modalTitle, setModalTitle] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [selectedAccessCount, setSelectedAccessCount] = useState('1');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedDuration, setSelectedDuration] = useState('');
  const [notes, setNotes] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [availableDates, setAvailableDates] = useState([]);
  const [datesLoading, setDatesLoading] = useState(false);

  const [activeStep, setActiveStep] = useState(0);

  const maxConcurrentAccess = reservable?.maxConcurrentAccess ?? 1;
  const multiAccess = maxConcurrentAccess > 1;
  const steps = multiAccess
    ? ['Accessi', 'Data', 'Orario', 'Conferma']
    : ['Data', 'Orario', 'Conferma'];

  const theme = useTheme();
  const isCompactStepper = useMediaQuery(theme.breakpoints.down('sm'));

  const accessStep = multiAccess ? 0 : -1;
  const dateStep = multiAccess ? 1 : 0;
  const timeStep = multiAccess ? 2 : 1;
  const confirmStep = multiAccess ? 3 : 2;

  useEffect(() => {
    fetchReservable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchReservable = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/reservables/${id}`);
      setReservable(response.data);
      if (response.data.availableDurations.length > 0) {
        setSelectedDuration(response.data.availableDurations[0].toString());
      }
    } catch (err) {
      setError('Errore nel caricamento della risorsa');
      console.error('Error fetching reservable:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableDates = useCallback(async () => {
    if (!selectedAccessCount) {
      setAvailableDates([]);
      return;
    }

    try {
      setDatesLoading(true);
      const response = await axios.get(`/api/reservables/${id}/available-dates`, {
        params: { accessCount: selectedAccessCount },
      });
      const dates = response.data;
      setAvailableDates(dates);
      if (selectedDate && !dates.includes(selectedDate)) {
        setSelectedDate('');
        setSelectedTime('');
        setAvailableSlots([]);
      }
    } catch (err) {
      console.error('Error fetching available dates:', err);
      setAvailableDates([]);
    } finally {
      setDatesLoading(false);
    }
  }, [id, selectedAccessCount, selectedDate]);

  const fetchAvailableSlots = useCallback(async () => {
    if (!selectedDate || !selectedDuration || !selectedAccessCount) {
      setAvailableSlots([]);
      return;
    }

    try {
      setSlotsLoading(true);
      const response = await axios.get(`/api/reservables/${id}/available-slots`, {
        params: {
          date: selectedDate,
          duration: selectedDuration,
          accessCount: selectedAccessCount,
        },
      });
      setAvailableSlots(response.data);
      if (selectedTime && !response.data.some((s) => s.time === selectedTime)) {
        setSelectedTime('');
      }
    } catch (err) {
      console.error('Error fetching available slots:', err);
      setAvailableSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  }, [id, selectedDate, selectedDuration, selectedAccessCount, selectedTime]);

  useEffect(() => {
    if (activeStep === dateStep) {
      fetchAvailableDates();
    }
  }, [activeStep, dateStep, fetchAvailableDates]);

  useEffect(() => {
    if (activeStep === timeStep && selectedDate && selectedDuration) {
      fetchAvailableSlots();
    }
  }, [activeStep, timeStep, selectedDate, selectedDuration, selectedAccessCount, fetchAvailableSlots]);

  const availableDateSet = new Set(availableDates);
  const isDateBookable = (date) => availableDateSet.has(date.format('YYYY-MM-DD'));

  const handleDurationChange = (duration) => {
    setSelectedDuration(duration);
    setSelectedTime('');
  };

  const calculatePrice = () => {
    if (selectedDuration && reservable) {
      const durationHours = parseInt(selectedDuration, 10) / 60;
      const accessCount = parseInt(selectedAccessCount, 10) || 1;
      return (durationHours * reservable.costPerHour * accessCount).toFixed(2);
    }
    return '0';
  };

  const handleSubmit = () => {
    if (!selectedDate || !selectedTime || !selectedDuration) {
      setError('Compila tutti i campi obbligatori');
      return;
    }
    setConfirmOpen(true);
  };

  const handleConfirmSubmit = async () => {
    setConfirmOpen(false);

    try {
      setSubmitting(true);
      setError(null);

      const reservationData = {
        reservableId: id,
        date: selectedDate,
        startTime: selectedTime,
        duration: parseInt(selectedDuration, 10),
        accessCount: parseInt(selectedAccessCount, 10) || 1,
        notes: notes.trim() || undefined,
      };

      await axios.post('/api/reservations', reservationData);

      setModalTitle('Prenotazione Creata');
      setModalMessage('La tua prenotazione è stata creata con successo! Riceverai una conferma via email.');
      setModalSeverity('success');
      setModalOpen(true);
    } catch (err) {
      setModalTitle('Errore Prenotazione');
      setModalMessage(err.response?.data?.message || 'Si è verificato un errore durante la creazione della prenotazione. Riprova più tardi.');
      setModalSeverity('error');
      setModalOpen(true);
      console.error('Error creating reservation:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelConfirm = () => {
    setConfirmOpen(false);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    navigate('/my-reservations');
  };

  const getCategoryLabel = (category) => {
    const labels = {
      sala: 'Sala',
      istruttore: 'Istruttore',
      attrezzatura: 'Attrezzatura',
    };
    return labels[category] || category;
  };

  const getCategoryColor = (category) => {
    const colors = {
      sala: 'primary',
      istruttore: 'secondary',
      attrezzatura: 'success',
    };
    return colors[category] || 'default';
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) {
      return `${minutes}min`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
  };

  const getDayName = (dayOfWeek) => {
    const days = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];
    return days[dayOfWeek];
  };

  const formatSlotLabel = (slot) => {
    const max = slot.maxConcurrentAccess ?? maxConcurrentAccess;
    return `${slot.time} — ${slot.remaining}/${max} posti`;
  };

  const maxBookingDate = reservable
    ? dayjs().add(reservable.advanceBookingDays ?? 14, 'day').format('YYYY-MM-DD')
    : dayjs().add(14, 'day').format('YYYY-MM-DD');

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error && !reservable) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/reservables')}
        >
          Torna alle prenotazioni
        </Button>
      </Container>
    );
  }

  return (
    <>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/reservables')}
            sx={{ mb: 2 }}
          >
            Torna alle prenotazioni
          </Button>

          <Typography variant="h4" gutterBottom>
            Prenota: {reservable?.name}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <Chip
              label={getCategoryLabel(reservable?.category)}
              color={getCategoryColor(reservable?.category)}
              size="small"
            />
            {multiAccess && (
              <Chip
                label={`Fino a ${maxConcurrentAccess} accessi contemporanei`}
                color="info"
                size="small"
              />
            )}
            {reservable?.requiresApproval && (
              <Chip
                label="Richiede approvazione"
                color="warning"
                size="small"
                icon={<Schedule />}
              />
            )}
          </Box>
        </Box>

        <Grid container spacing={4}>
          <Grid size={{ xs: 12, lg: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Dettagli Risorsa
                </Typography>

                {reservable?.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {reservable.description}
                  </Typography>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <LocationOn sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    {reservable?.location || 'Non specificata'}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <People sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    Capacità: {reservable?.capacity} {reservable?.capacity === 1 ? 'persona' : 'persone'}
                  </Typography>
                </Box>

                {multiAccess && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <People sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      Accessi contemporanei: {maxConcurrentAccess}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Euro sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    {reservable?.costPerHour}€/ora{multiAccess ? ' per accesso' : ''}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AccessTime sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    Durate disponibili: {reservable?.availableDurations.map((d) => formatDuration(d)).join(', ')}
                  </Typography>
                </Box>

                {reservable?.teacher && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Person sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      Istruttore: {reservable.teacher.name} {reservable.teacher.surname}
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" gutterBottom>
                  Disponibilità:
                </Typography>
                {reservable?.availability.map((avail, index) => (
                  <Chip
                    key={index}
                    label={`${getDayName(avail.dayOfWeek)} ${avail.startTime}-${avail.endTime}`}
                    size="small"
                    sx={{ mr: 1, mb: 1 }}
                  />
                ))}
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, lg: 6 }}>
            <Paper sx={{ p: 3, overflow: 'hidden' }}>
              <Typography variant="h6" gutterBottom>
                Crea Prenotazione
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Stepper
                activeStep={activeStep}
                orientation={isCompactStepper ? 'vertical' : 'horizontal'}
                alternativeLabel={!isCompactStepper}
                sx={{
                  mb: 3,
                  '& .MuiStepLabel-label': {
                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                    lineHeight: 1.25,
                    whiteSpace: 'normal',
                    textAlign: isCompactStepper ? 'left' : 'center',
                    mt: isCompactStepper ? 0 : 0.5,
                  },
                  '& .MuiStepConnector-line': {
                    borderTopWidth: isCompactStepper ? undefined : 2,
                  },
                }}
              >
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              {activeStep === accessStep && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Quanti accessi vuoi prenotare?
                  </Typography>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Numero accessi</InputLabel>
                    <Select
                      value={selectedAccessCount}
                      onChange={(e) => {
                        setSelectedAccessCount(e.target.value);
                        setSelectedTime('');
                        setAvailableSlots([]);
                      }}
                      label="Numero accessi"
                    >
                      {Array.from({ length: maxConcurrentAccess }, (_, i) => i + 1).map((n) => (
                        <MenuItem key={n} value={String(n)}>
                          {n} access{n === 1 ? 'o' : 'i'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      onClick={() => setActiveStep(dateStep)}
                      disabled={!selectedAccessCount}
                    >
                      Avanti
                    </Button>
                  </Box>
                </Box>
              )}

              {activeStep === dateStep && (
                <Box>
                  {multiAccess && (
                    <Box sx={{ mb: 2, p: 2, backgroundColor: 'primary.light', borderRadius: 1, color: 'primary.contrastText' }}>
                      <Typography variant="body2">
                        <strong>Accessi:</strong> {selectedAccessCount}
                      </Typography>
                    </Box>
                  )}
                  {datesLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                      <CircularProgress size={28} />
                    </Box>
                  ) : availableDates.length === 0 ? (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Nessuna data disponibile per il numero di accessi selezionato
                    </Alert>
                  ) : (
                    <DatePicker
                      label="Data"
                      value={selectedDate ? dayjs(selectedDate) : null}
                      onChange={(newValue) => {
                        setSelectedDate(newValue ? newValue.format('YYYY-MM-DD') : '');
                        setSelectedTime('');
                        setAvailableSlots([]);
                      }}
                      format="DD/MM/YYYY"
                      minDate={dayjs()}
                      maxDate={dayjs(maxBookingDate)}
                      shouldDisableDate={(date) => !isDateBookable(date)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          sx: { mb: 2 },
                          helperText: 'Solo i giorni con disponibilità effettiva sono selezionabili',
                        },
                      }}
                    />
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    {multiAccess && (
                      <Button variant="outlined" onClick={() => setActiveStep(accessStep)}>
                        Indietro
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      onClick={() => setActiveStep(timeStep)}
                      disabled={!selectedDate || datesLoading || availableDates.length === 0}
                      sx={{ ml: 'auto' }}
                    >
                      Avanti
                    </Button>
                  </Box>
                </Box>
              )}

              {activeStep === timeStep && (
                <Box>
                  {selectedDate && (
                    <Box sx={{ mb: 2, p: 2, backgroundColor: 'primary.light', borderRadius: 1, color: 'primary.contrastText' }}>
                      <Typography variant="body2">
                        <strong>Data:</strong> {getDayName(new Date(selectedDate).getDay())}{' '}
                        {dayjs(selectedDate).format('DD-MMM-YY')}
                        {multiAccess && (
                          <>
                            {' · '}
                            <strong>Accessi:</strong> {selectedAccessCount}
                          </>
                        )}
                      </Typography>
                    </Box>
                  )}

                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Durata</InputLabel>
                    <Select
                      value={selectedDuration}
                      onChange={(e) => handleDurationChange(e.target.value)}
                      label="Durata"
                    >
                      {reservable?.availableDurations.map((duration) => {
                        const accessCount = parseInt(selectedAccessCount, 10) || 1;
                        const price = (duration / 60) * reservable.costPerHour * accessCount;
                        return (
                          <MenuItem key={duration} value={String(duration)}>
                            {formatDuration(duration)} - {price.toFixed(2)}€
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>

                  {selectedDate && selectedDuration ? (
                    slotsLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <CircularProgress size={28} />
                      </Box>
                    ) : availableSlots.length > 0 ? (
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Orario</InputLabel>
                        <Select
                          value={selectedTime}
                          onChange={(e) => setSelectedTime(e.target.value)}
                          label="Orario"
                        >
                          {availableSlots.map((slot) => (
                            <MenuItem key={slot.time} value={slot.time}>
                              {formatSlotLabel(slot)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        Nessun orario disponibile per questa data, durata e numero di accessi
                      </Alert>
                    )
                  ) : (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Seleziona data e durata per vedere gli orari disponibili
                    </Alert>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Button variant="outlined" onClick={() => setActiveStep(dateStep)}>
                      Indietro
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => setActiveStep(confirmStep)}
                      disabled={!selectedTime}
                    >
                      Avanti
                    </Button>
                  </Box>
                </Box>
              )}

              {activeStep === confirmStep && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Riepilogo Prenotazione
                  </Typography>

                  <Box sx={{ mb: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                    {multiAccess && (
                      <Typography variant="body2" color="text.secondary">
                        <strong>Accessi:</strong> {selectedAccessCount}
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      <strong>Data:</strong> {getDayName(new Date(selectedDate).getDay())}{' '}
                      {dayjs(selectedDate).format('DD-MMM-YY')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Orario:</strong> {selectedTime} -{' '}
                      {(() => {
                        const startMinutes = selectedTime
                          .split(':')
                          .reduce((hours, mins) => hours * 60 + parseInt(mins, 10), 0);
                        const endMinutes = startMinutes + parseInt(selectedDuration, 10);
                        const endHours = Math.floor(endMinutes / 60);
                        const endMins = endMinutes % 60;
                        return `${endHours.toString().padStart(2, '0')}:${endMins.toString().padStart(2, '0')}`;
                      })()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Durata:</strong> {formatDuration(parseInt(selectedDuration, 10))}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Prezzo:</strong> {calculatePrice()}€
                    </Typography>
                  </Box>

                  <TextField
                    fullWidth
                    label="Note (opzionale)"
                    multiline
                    rows={3}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    sx={{ mb: 2 }}
                  />

                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button variant="outlined" onClick={() => setActiveStep(timeStep)} fullWidth>
                      Indietro
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleSubmit}
                      disabled={submitting}
                      startIcon={submitting ? <CircularProgress size={20} /> : <CheckCircle />}
                      fullWidth
                    >
                      {submitting ? 'Creando...' : 'Conferma Prenotazione'}
                    </Button>
                  </Box>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>

      <ModalConfirm
        open={confirmOpen}
        title="Conferma Prenotazione"
        message={`Sei sicuro di voler creare questa prenotazione per ${reservable?.name} il ${getDayName(new Date(selectedDate).getDay())} ${dayjs(selectedDate).format('DD-MMM-YY')} alle ${selectedTime}${multiAccess ? ` (${selectedAccessCount} accessi)` : ''}?`}
        onConfirm={handleConfirmSubmit}
        onCancel={handleCancelConfirm}
      />

      <ModalResult
        open={modalOpen}
        onClose={handleModalClose}
        message={modalMessage}
        severity={modalSeverity}
        title={modalTitle}
      />
    </>
  );
};

export default ReservationDetail;
